import * as React from "react";
import * as _ from "lodash";
import classNames from "classnames";
import DomHandler from "@bizform/core/dist/js/vendor/primereact/utils/DomHandler";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import { ComponentElementPosition } from "@bizform/type/model/data_set/LookUp.model";
import {
    ComponentElementBorderType,
    ComponentElementOverflowType,
    JSMap
} from "@bizform/type/model/component_element/ComponentElement.model";
import { ComponentHelper, DomHelper, StringHelper, FunctionHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { EventResize, ProxyBaseClass } from "@bizform/core/dist/js/bizflow/core.min";
import TabControlHelper from "./TabControlHelper";
import { TabViewOnTabChangeEventType } from "../BfTabViewPanel";
import { HiddenButtonPanel } from "./HiddenButtonPanel";
import { HiddenButtonItem } from "./HiddenButtonItem";
import { TabModel } from "./Tab.model";


type Props = ComponentElementOverflowType & ComponentElementBorderType & {
    id?: string;
    activeIndex?: number;
    style?: any;
    className?: string;
    renderActiveOnly?: boolean;
    children?: any;
    tabVisibilities?: Array<boolean>;
    tabPosition?: string;
    contentHeight?: string;
    accessibility?: boolean;
    onTabChange?(e: TabViewOnTabChangeEventType): void;
    handleTabVisibility?(v: Array<boolean>, callback?: Function, defaultCallback?: Function): void;
    getCalcVisibilityDone(): boolean;
    setCalcVisibilityDone(f: boolean): void;
    getTabModel(): Array<TabModel>;
};

type State = {
    id?: string;
    activeIndex?: number;
    style?: any;
    className?: string;
    renderActiveOnly?: boolean;
    tabPosition?: ComponentElementPosition;
    border?: number;
    // isFirstRender: boolean;
    // needReRenderAfterDelOrInsTab?: boolean;
};

type Dimensions = {
    width?: number;
    height?: number;
};

type HiddenButtons = {
    iconNodeCount: number;
    hidNode: Array<React.ReactNode>;
};

/**
 * TabViewContainer component
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ProxyBaseClass("low", "componentWillUnmount")
export class TabSet extends React.Component {

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {
    };

    private readonly _id: string;
    private _tabControlHelper: TabControlHelper;
    private _state = {
        needVisibilityCalc: false
    };

    // navigation bar element의 ref
    private _tabNavRef: HTMLUListElement;
    // 각각의 tab element의 ref
    private readonly _tabRefs: Array<HTMLAnchorElement>;
    // tab panel element의 ref
    private _tabPanelRef: HTMLDivElement;

    private _hiddenButtonContainerRef: any;
    private _hiddenButtonPanelRef: any;
    private _documentClickListener: any;
    private _hiddenButtonGroupClick: boolean;

    constructor(props?: Props) {
        super(props);
        this._id = this.props.id || StringHelper.randomString();
        this._tabControlHelper = new TabControlHelper(this);

        const childrenCount = this.props.children.length;
        this._tabRefs = new Array<HTMLAnchorElement>();
        this._tabRefs.length = childrenCount;

        this.state = {
            activeIndex: this.props.activeIndex || 0
            // isFirstRender: true,
            // needReRenderAfterDelOrInsTab: true
        };

        this._resize = this._resize.bind(this);
        this._setTabNavRef = this._setTabNavRef.bind(this);
        this._onHiddenButtonClick = this._onHiddenButtonClick.bind(this);
        this._hideHiddenButton = this._hideHiddenButton.bind(this);
    }

    public getTabNavRef(): HTMLUListElement {
        return this._tabNavRef;
    }

    public getTabRefs(): Array<HTMLAnchorElement> {
        return this._tabRefs;
    }

    public getTabPanelRef(): HTMLDivElement {
        return this._tabPanelRef;
    }

    public getProps(): Readonly<Props> {
        return this.props;
    }

    public getState(): Readonly<State> {
        return this.state;
    }

    public getTabControlHelper(): TabControlHelper {
        return this._tabControlHelper;
    }

    componentDidMount(): void {
        this._tabControlHelper.setInitTabNavHeight(this.getTabNavRef().getBoundingClientRect().height);
        this._handleTabVisibility(null, () => {
            this.setState({});
        });
        // window.addEventListener("resize", this._resize);
        // this.setState({isFirstRender: false});
        /*
         * tabview는 mount후 tab visibility 계산이 필요하므로 다시 한번 render life cycle을 호출한다.
         */
    }

    /**
     * 탭 컴포넌트를 감싸는 레이아웃 컴포넌트가 display none 상태로 진입한 뒤 다시 렌더링 되면
     * ul 영역의 width, height가 0으로 계산되어지는 버그로 인해 아래와 같이 async로 구현
     * @param prevProps
     * @param prevState
     */
    async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): Promise<void> {
        await FunctionHelper.timeout();
        // this._handleTabVisibility();

        if (this._state.needVisibilityCalc) {
            /* this._state.needVisibilityCalc가 truthy면 this.props.setCalcVisibilityDone()가 falsy 임을 의미한다. */
            this._state.needVisibilityCalc = false;
            this._handleTabVisibility(null, () => {
                this.setState({});
            });
        } else {
            /*
             * modal에 tabview 컴포넌트를 삽입하는 경우에는 모달이 초기에 미리 생성되기 때문에
             * tab height(& width, top and left)가 0으로 계산되어지므로 _updateRect 메소드 호출이 필요하다.
             */
            const tabNavRef = this.getTabNavRef();
            const tabRects = this.getTabControlHelper().getTabRects();
            const firstTabRectHeight = tabRects && tabRects[0] ? tabRects[0].height : -1;
            if (tabNavRef && tabNavRef.offsetHeight > 0 && firstTabRectHeight === 0) {
                this.props.setCalcVisibilityDone(false);
                this._handleTabVisibility(null, () => {
                    this.setState({});
                });
            } else {
                this._handleTabVisibility();
            }
        }
    }

    componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any): void {
        if (nextProps.children) {
            this._tabRefs.length = 0;
            const childrenCount = nextProps.children.length;
            this._tabRefs.length = childrenCount;

            if (!nextProps.getCalcVisibilityDone()) {
                this._state.needVisibilityCalc = true;
            }

            if (nextProps.activeIndex > childrenCount - 1) {
                this.setState({activeIndex: 0});
            }
        }
    }

    componentWillUnmount(): void {
        this._unbindDocumentListener();
    }

    @EventResize()
    private _resize(): void  {
        this._handleTabVisibility(this._hideHiddenButton);
    }

    private _handleTabVisibility(callback?: Function, defaultCallback?: Function) {
        const newTabVisibilities = this.getTabControlHelper().performTabVisibilitySetting();
        this.props.handleTabVisibility(newTabVisibilities, callback, defaultCallback);
    }

    isSelected(index) {
        const activeIndex = this.props.onTabChange ? this.props.activeIndex : this.state.activeIndex;
        const actIndexNo = (typeof activeIndex === "string") ? parseInt(activeIndex, 10) : activeIndex;

        return (actIndexNo === index);
    }

    onTabHeaderClick(event, tab, index) {
        if (!tab.props.disabled) {
            if (event.key && event.key !== "Enter") {
                return;
            }
            if (this.props.onTabChange) {
                this.props.onTabChange({originalEvent: event, index: index, tabUniqueKey: tab.props.tabUniqueKey});
                // TODO: BfTabView의 onChange 핸들러를 고려하여 개발 필요
                this.setState({ activeIndex: index });
            } else {
                // setState
                this.setState({ activeIndex: index });
            }
        }

        // console.log(event);
        event.preventDefault();
    }

    renderTabHeader(tab, index) {
        const selected = this.isSelected(index);
        const className = classNames(tab.props.headerClassName, "p-unselectable-text m-0", {"p-tabview-selected p-highlight": selected, "p-disabled": tab.props.disabled, "float-left": this.props.accessibility});
        const id = this._id + "_header_" + index;
        const ariaControls = this._id + "_content_" + index;

        let liStyle = null;
        let aStyle = null;

        const tabPosition = this.props.tabPosition;

        if (this.props.getCalcVisibilityDone()) {
            if (tabPosition === ComponentElementPosition.RIGHT || tabPosition === ComponentElementPosition.LEFT) {
                liStyle = {height: this.getTabControlHelper().getTabRectHeight(index) + "px", top: 0, bottom: 0};
            } else {
                liStyle = {top: 0, bottom: 0};
            }
        }

        if (tabPosition === ComponentElementPosition.RIGHT) {
            aStyle = { transform: "rotate(-270deg) translateY(-100%)", transformOrigin: "top left", margin: 0 };
        } else if (tabPosition === ComponentElementPosition.LEFT) {
            aStyle = { transform: "rotate(-90deg) translateX(-100%)", transformOrigin: "top left", margin: 0 };
        } else {
            aStyle = {};
        }

        const tabModel = this.props.getTabModel();
        const iconClass = tabModel[index] && tabModel[index].iconClass ? _.trim(tabModel[index].iconClass) : null;

        /* removed href={"#" + ariaControls} in anchor tag, 2019.03.19 by k2webma */
        return (
            this.props.accessibility || this.props.tabVisibilities[index] ?
            <li className={className} role="presentation"
                style={liStyle}
            >
                <a
                    role="tab"
                    tabIndex={0}
                    onClick={(event) => this.onTabHeaderClick(event, tab, index)}
                    onKeyDown={(event) => this.onTabHeaderClick(event, tab, index)}
                    id={id}
                    aria-controls={ariaControls}
                    aria-selected={selected}
                    ref={(ref) => this._tabRefs[index] = (ref === null) ? undefined : ref}
                    style={aStyle}
                >
                    { iconClass ? <i className={iconClass}></i> : "" }
                    <span className="p-tabview-title">{tab.props.header}</span>
                </a>
            </li> : null
        );
    }

    renderTabHeaders() {
        return (
            React.Children.map(this.props.children, (tab, index) => {
                // console.log(index, tab);
                // console.log(this.props.children);
                return this.renderTabHeader(tab, index);
            })
        );
    }

    renderNavigator() {
        const { tabPosition } = this.props;
        const headers = this.renderTabHeaders();
        const className = "p-tabview-nav p-reset d-flex flex-wrap bf-tabview-" + tabPosition + " " + this._getNavigatorFlexDirection();
        const style = this.props.accessibility ? null : this._tabControlHelper.getAllowedNavbarSizeAsStyle();

        return (
            <ul className={className}
                role="tablist"
                ref={this._setTabNavRef}
                style={{...style, flex: "0 0 auto"}}
            >
                {headers}
            </ul>
        );
    }

    private _getProperStyle(): JSMap<any> {
        const overflow = this.props.overflow;
        const style = ComponentHelper.makeOverflowStyle(overflow);

        style.flex = "1 1 auto";
        style.minHeight = 0;
        style.height = this.props.contentHeight ? this.props.contentHeight : null;

        return style;
    }

    renderContent() {
        const { borderTop, borderRight, borderBottom, borderLeft } = this.props;
        const border = { borderTop, borderRight, borderBottom, borderLeft };
        /* added d-flex for safari, 2019.04.09 */
        const className = ComponentHelper.makeBorderStyle("p-tabview-panels p-0 d-flex", border);
        const contents = React.Children.map(this.props.children, (tab, index) => {
            if (!this.props.renderActiveOnly || this.isSelected(index)) {
                return this.createContent(tab, index);
            }
        });

        return (
            <div className={className}
                 style={this._getProperStyle()}
                 ref={(ref) => this._tabPanelRef = (ref === null) ? undefined : ref}
            >
                {contents}
            </div>
        );
    }

    createContent(tab, index) {
        const selected = this.isSelected(index);
        /* removed h-100 for safari instead added flex-fill, 2019.04.09 */
        const className = classNames(tab.props.contentClassName, "p-tabview-panel w-100 p-0 flex-fill", {"p-hidden": !selected});
        const id = this._id + "_content_" + index;
        const ariaLabelledBy = this._id + "_header_" + index;
        const tabUniqueKey = tab.props.tabUniqueKey ? tab.props.tabUniqueKey : "";
        const bfContainer = {[DomAttribute.CONTAINER.toString()]: tabUniqueKey};

        return (
            <div id={id} aria-labelledby={ariaLabelledBy} aria-hidden={!selected} className={className}
                style={tab.props.contentStyle} role="tabpanel" {...bfContainer}
            >
                {!this.props.renderActiveOnly ? tab.props.children : (selected && tab.props.children)}
            </div>
        );
    }

    renderHidTabBox() {
        const btnStyle = this._tabControlHelper.getHidTabBoxStyleWithPosition();
        const hiddenButtons = this._renderHiddenButtonItems();

        return (
            hiddenButtons.hidNode.length > 0 ?
                <React.Fragment>
                    <button className="bf-btn-tab-more"
                            type="button"
                            style={btnStyle}
                            onClick={this._onHiddenButtonClick}
                            ref={(el) => this._hiddenButtonContainerRef = el}
                    >
                        {
                            hiddenButtons.iconNodeCount > 0 ?
                                <i className="fas fa-plus-circle bf-text-red5"></i>
                                :
                                <i className="fas fa-plus"></i>
                        }
                        <span className={hiddenButtons.iconNodeCount > 0 ? "bf-text-red5" : null}>{hiddenButtons.hidNode.length}</span>
                    </button>
                    <HiddenButtonPanel appendTo={DomHelper.getForOverLayDom()}
                                       ref={(el) => this._hiddenButtonPanelRef = el}
                    >
                        {hiddenButtons.hidNode}
                    </HiddenButtonPanel>
                </React.Fragment>
                : null
        );
    }

    private _renderHiddenButtonItems(): HiddenButtons {
        let iconNodeCount = 0;
        const retNodes = [];
        const tabModel = this.props.getTabModel();
        const hidItems = [];
        for (let idx = 0; idx < this.props.tabVisibilities.length; ++idx) {
            if (!this.props.tabVisibilities[idx]) {
                hidItems.push({
                    label: tabModel[idx].label,
                    command: (event) => this.onTabHeaderClick(event, this.props.children[idx], idx),
                    iconClass: this.props.getTabModel()[idx].iconClass
                });
                if (_.trim(this.props.getTabModel()[idx].iconClass)) {
                    ++iconNodeCount;
                }
            }
        }
        if (hidItems) {
            hidItems.map((menuitem, index) => {
                retNodes.push(<HiddenButtonItem menuItem={menuitem} key={index} />);
            });
        }

        return {
            iconNodeCount: iconNodeCount,
            hidNode: retNodes
        };
    }

    render() {
        const className = classNames("p-tabview p-component p-tabview-top h-100 d-flex p-0 " + this._getTabsetFlexDirection(), this.props.className);
        const navigator = this.renderNavigator();
        const content = this.renderContent();
        const hidTabBox = !this.props.accessibility ? this.renderHidTabBox() : null;
        const { tabPosition } = this.props;

        if (tabPosition && (tabPosition === ComponentElementPosition.BOTTOM || tabPosition === ComponentElementPosition.RIGHT)) {
            return (
                <div className="h-100 position-relative">
                    <div id={this.props.id} className={className} style={{ ...this.props.style }}>
                        {content}
                        {navigator}
                    </div>
                    {hidTabBox}
                </div>
            );
        }

        return (
            <div className="h-100 position-relative">
                <div id={this.props.id} className={className} style={{ ...this.props.style }}>
                    {navigator}
                    {content}
                </div>
                {hidTabBox}
            </div>
        );
    }

    private _getTabsetFlexDirection(): string {
        const tabPosition = this.props.tabPosition;
        return (tabPosition && (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM))
            ? "flex-column" : "flex-row";
    }

    private _getNavigatorFlexDirection(): string {
        const tabPosition = this.props.tabPosition;
        return (tabPosition && (tabPosition === ComponentElementPosition.TOP || tabPosition === ComponentElementPosition.BOTTOM))
            ? "flex-row" : "flex-column";
    }

    private _setTabNavRef(ref) {
        this._tabNavRef = (ref === null) ? undefined : ref;
    }

    private _onHiddenButtonClick(event) {
        if (this._documentClickListener) {
            this._hiddenButtonGroupClick = true;
        }

        if (this._hiddenButtonPanelRef.getElement().offsetParent)
            this._hideHiddenButton();
        else
            this._showHiddenButton();
    }

    private _showHiddenButton(): void {
        this._hiddenButtonPanelRef.getElement().style.zIndex = String(DomHandler.generateZIndex());
        this._hiddenButtonPanelRef.getElement().style.display = "block";

        setTimeout(() => {
            DomHandler.addClass(this._hiddenButtonPanelRef.getElement(), "p-menu-overlay-visible");
            DomHandler.removeClass(this._hiddenButtonPanelRef.getElement(), "p-menu-overlay-hidden");
        }, 1);

        this._alignHiddenButtonPanel();
        this._bindDocumentListener();
    }

    private _hideHiddenButton() {
        if (this._hiddenButtonPanelRef && this._hiddenButtonPanelRef.getElement()) {
            DomHandler.addClass(this._hiddenButtonPanelRef.getElement(), "p-menu-overlay-hidden");
            DomHandler.removeClass(this._hiddenButtonPanelRef.getElement(), "p-menu-overlay-visible");

            setTimeout(() => {
                if (this._hiddenButtonPanelRef && this._hiddenButtonPanelRef.getElement()) {
                    this._hiddenButtonPanelRef.getElement().style.display = "none";
                    DomHandler.removeClass(this._hiddenButtonPanelRef.getElement(), "p-menu-overlay-hidden");
                }
            }, 150);
        }

        this._unbindDocumentListener();
        this._hiddenButtonGroupClick = false;
    }

    private _alignHiddenButtonPanel(): void {
        this._hiddenButtonPanelRef.getElement().style.minWidth = DomHandler.getWidth(this._hiddenButtonContainerRef) + "px";
        this._absolutePosition(this._hiddenButtonPanelRef.getElement(), this._hiddenButtonContainerRef);
    }

    private _bindDocumentListener(): void {
        if (!this._documentClickListener) {
            this._documentClickListener = () => {
                if (this._hiddenButtonGroupClick)
                    this._hiddenButtonGroupClick = false;
                else
                    this._hideHiddenButton();
            };

            document.addEventListener("click", this._documentClickListener);
        }
    }

    private _unbindDocumentListener(): void {
        if (this._documentClickListener) {
            document.removeEventListener("click", this._documentClickListener);
            this._documentClickListener = null;
        }
    }

    private _absolutePosition(element, target): void {
        const elementDimensions: Dimensions = element.offsetParent ? {
            width: element.offsetWidth,
            height: element.offsetHeight
        } : this._getHiddenElementDimensions(element);
        const elementOuterHeight = elementDimensions.height;
        const elementOuterWidth = elementDimensions.width;
        const targetOuterHeight = target.offsetHeight;
        const targetOuterWidth = target.offsetWidth;
        const targetOffset = target.getBoundingClientRect();
        const windowScrollTop = this._getWindowScrollTop();
        // const windowScrollLeft = this._getWindowScrollLeft();
        const viewport = this._getViewport();
        const { tabPosition } = this.props;
        let top, left;

        if (tabPosition === ComponentElementPosition.TOP) {
            if (targetOffset.top + targetOuterHeight + elementOuterHeight > viewport.height) {
                top = targetOffset.top + windowScrollTop - elementOuterHeight;
                if (top < 0) {
                    top = windowScrollTop;
                }
            } else {
                top = targetOuterHeight + targetOffset.top + windowScrollTop;
            }

            left = targetOffset.left + targetOuterWidth - elementOuterWidth;
        } else if (tabPosition === ComponentElementPosition.RIGHT) {
            // 잠깐 스크롤바가 생겼다 사라지는 현상(chrome) -> Math.floor로 소수점 이하 제거
            top = Math.floor(targetOuterHeight + targetOffset.top + windowScrollTop - elementOuterHeight);
            if (top < 0) {
                top = windowScrollTop;
            }

            left = targetOffset.left - elementOuterWidth;
        } else if (tabPosition === ComponentElementPosition.BOTTOM) {
            top = targetOffset.top + windowScrollTop - elementOuterHeight;
            if (top < 0) {
                top = windowScrollTop;
            }

            left = targetOffset.left + targetOuterWidth - elementOuterWidth;
        } else {
            // 잠깐 스크롤바가 생겼다 사라지는 현상(chrome) -> Math.floor로 소수점 이하 제거
            top = Math.floor(targetOuterHeight + targetOffset.top + windowScrollTop - elementOuterHeight);
            if (top < 0) {
                top = windowScrollTop;
            }

            left = targetOffset.left + targetOuterWidth;
        }

        element.style.top = top + "px";
        element.style.left = left + "px";
    }

    private _getHiddenElementDimensions(element): Dimensions {
        const dimensions: Dimensions = {};
        element.style.visibility = "hidden";
        element.style.display = "block";
        dimensions.width = element.offsetWidth;
        dimensions.height = element.offsetHeight;
        element.style.display = "none";
        element.style.visibility = "visible";
        return dimensions;
    }

    /**
     * 문서가 수직으로 스크롤된 경우 스크롤된 높이
     * @private
     */
    private _getWindowScrollTop(): number {
        const doc = document.documentElement;
        return (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    }

    /**
     * 문서가 좌우로 스크롤된 경우 스크롤된 너비
     * @private
     */
    private _getWindowScrollLeft(): number {
        const doc = document.documentElement;
        return (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    }

    private _getViewport(): Dimensions {
        const win = window,
            d = document,
            e = d.documentElement,
            g = d.getElementsByTagName("body")[0],
            w = win.innerWidth || e.clientWidth || g.clientWidth,
            h = win.innerHeight || e.clientHeight || g.clientHeight;
        return {
            width: w,
            height: h
        };
    }
}
